import { Link } from "react-router-dom";
import { GraduationCap, Sparkles, Brain, Code2, Trophy, ArrowRight, Github, Youtube, PlayCircle, BookOpen, Users, ShieldCheck } from "lucide-react";

const FOUNDER = {
  github: "https://github.com/bharanisivakumar6374-cmd",
  youtube: "https://www.youtube.com/watch?v=x09MECz3F5A",
};

export default function HomePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <header className="bg-[#0B1120] text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
            <div>
              <div className="font-display font-black text-lg leading-none">TrillionBytex<span className="text-blue-400">edu</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Smart TBX Learning Hub</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <Link to="/about" data-testid="home-nav-about" className="hover:text-white">About</Link>
            <Link to="/login" data-testid="home-nav-courses" className="hover:text-white">Courses</Link>
            <Link to="/login" data-testid="home-nav-games" className="hover:text-white">Brain Games</Link>
            <Link to="/login" data-testid="home-nav-coding" className="hover:text-white">Coding Practice</Link>
            <Link to="/contact" data-testid="home-nav-contact" className="hover:text-white">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="home-signin" className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white">Sign in</Link>
            <Link to="/register" data-testid="home-register" className="px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-sm">Register</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-3">AI-powered · Career-focused</div>
            <h1 className="font-display font-black text-5xl md:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              Learn <span className="text-blue-600">smarter,</span><br />
              code <span className="text-emerald-600">bolder,</span><br />
              grow <span className="text-violet-600">daily.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              A modern learning hub with live classes, in-browser coding practice (JS · HTML · CSS · Python · C · C++), brain-training games, and <span className="font-semibold text-slate-900">02</span> — your personal AI mentor.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
                Get started free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/about" data-testid="hero-cta-about" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors">
                Learn more
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
              <span>Trusted at TBX Institute</span>
              <a href={FOUNDER.github} target="_blank" rel="noreferrer" className="hover:text-slate-900 inline-flex items-center gap-1"><Github className="w-3.5 h-3.5" /> GitHub</a>
              <a href={FOUNDER.youtube} target="_blank" rel="noreferrer" className="hover:text-red-500 inline-flex items-center gap-1"><Youtube className="w-3.5 h-3.5" /> YouTube</a>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-[#0B1120] text-white rounded-3xl p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-blue-500/25 blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-violet-500/25 blur-3xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400">Live now</div>
                <div className="mt-2 space-y-3">
                  <MiniCourse title="Full-Stack Web Dev" tag="Engineering · 10w" color="bg-blue-500" />
                  <MiniCourse title="Data Science with Python" tag="Data · 8w" color="bg-emerald-500" />
                  <MiniCourse title="UI/UX Design Foundations" tag="Design · 6w" color="bg-violet-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-20 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 mb-2">What&apos;s inside</div>
          <h2 className="font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight max-w-2xl">Everything a curious learner needs, in one place.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature icon={Sparkles} title="02 · AI Mentor" color="bg-violet-50 text-violet-700"
              body="Course recommendations, study plans, coding hints, quiz generation — powered by Gemini 3 Flash." />
            <Feature icon={Brain} title="Brain Games" color="bg-blue-50 text-blue-700"
              body="Memory Match, Sudoku, Pattern Recall, Typing Speed, Math Challenge, Slider Puzzle." />
            <Feature icon={Code2} title="Coding Practice" color="bg-emerald-50 text-emerald-700"
              body="Beginner-friendly challenges in JS, HTML/CSS, Python, C, C++. Live code editor, instant output." />
            <Feature icon={BookOpen} title="Courses & Live Classes" color="bg-amber-50 text-amber-700"
              body="Structured cohorts, uploaded notes and videos, Zoom join links, deadlines, and certificates." />
            <Feature icon={Trophy} title="Streaks & Leaderboards" color="bg-pink-50 text-pink-700"
              body="Daily learning streak, unlockable achievements, and a live leaderboard to keep you motivated." />
            <Feature icon={ShieldCheck} title="Safe & Educational" color="bg-slate-100 text-slate-700"
              body="Approved staff. Terms enforced. Login history tracked. Built for learners, not distractions." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1120] text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400 mb-3">Ready?</div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-tight">
            Start learning something new <span className="text-blue-400">today.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="cta-register" className="px-6 py-3 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100">Create account</Link>
            <Link to="/login" data-testid="cta-signin" className="px-6 py-3 rounded-full border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500">I already have one</Link>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
          <div>© {new Date().getFullYear()} TrillionBytexedu · Built by Bharani Sivakumar</div>
          <div className="flex items-center gap-4">
            <Link to="/about" className="hover:text-blue-400">About</Link>
            <Link to="/contact" className="hover:text-emerald-400">Contact</Link>
            <a href={FOUNDER.github} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-1"><Github className="w-3.5 h-3.5" /> GitHub</a>
            <a href={FOUNDER.youtube} target="_blank" rel="noreferrer" className="hover:text-red-500 inline-flex items-center gap-1"><Youtube className="w-3.5 h-3.5" /> YouTube</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, body, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 card-hover">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
      <h3 className="mt-3 font-display font-bold text-lg text-slate-900">{title}</h3>
      <p className="mt-1 text-sm text-slate-500 leading-relaxed">{body}</p>
    </div>
  );
}

function MiniCourse({ title, tag, color }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}><PlayCircle className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{title}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{tag}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-500" />
    </div>
  );
}
